import {Injectable} from '@angular/core';
import {BLE} from "@awesome-cordova-plugins/ble/ngx";
import {ProgramStep, SpinalLocation} from "../model/weak-entities";
import {BleCore, CommandDefinitions} from "./BleCore";
import {EmgConfig, EmgDataReading, EmgStatus} from "../model/emg";
import {ClaEvents} from "../model/events";
import {Events} from "ionic-angular";
import {numberFormat} from "highcharts";
import {formatError} from "awesome-typescript-loader/dist/helpers";
import {delay} from "../utils/utils";
import {ThermalStatus} from "../model/thermal";
import {LogService} from "../providers/logging/log.service";

@Injectable()
export class BleEmg extends BleCore {
  deviceId: string = 'A6548E12-B8F5-C2E7-A027-694589C63B27';
  BufferLength = 512;
  Pointer: number;
  TriggerTimeout: number;
  TriggerProcessing: boolean;

  HandheldProgramSteps: string[] = [];
  ProgramSteps: ProgramStep[] = [];
  ProgramCounter: number;
  Readings: EmgDataReading[] = [];
  CurrentReading: EmgDataReading;
  UpdateingNotch: number;
  Config: EmgConfig;
  Status: EmgStatus;
  connecting: boolean = false;

  constructor(private ble: BLE, public events: Events, public logger: LogService) {
    super(ble, events, logger);
    this.TriggerTimeout = Date.now();

  }

  public async Connect(id: string) : Promise<void> {
    this.events.subscribe(ClaEvents.DEVICE_CONNECTED, state => {
      this.logger.info(`DEVICE_CONNECTED ${state}`, [{'SourceContext': 'BleEmg'}]);
      if(!this.connecting) return;

      this.connected = state;
      this.connecting = false;
      this.events.publish(ClaEvents.EMG_CONNECTED, true);
      this.events.unsubscribe(ClaEvents.DEVICE_CONNECTED);
    });
    await this.SetupDevice(id);
    this.connecting = true;
  }

  public async Disconnect() {
    //await this.StreamEnable(EmgStreams.Rms, false);
    //await this.StreamEnable(EmgStreams.Ui, false);
    await super.Disconnect();
    this.events.publish(ClaEvents.EMG_DISCONNECTED, "");
  }

  public async StartScanning(): Promise<boolean> {
    await this.StartStream();
    this.ResetData();
    this.events.publish(ClaEvents.EMG_LISTENING, "");
    return true;
  }

  private async StartStream(): Promise<boolean> {
    let handheldProgram = BleEmg.GetProgram(this.HandheldProgramSteps, this.logger);
    await this.EmgLoadProgram(handheldProgram);
    this.Pointer = 0;
    await this.EmgProgramStep(this.Pointer);
    await this.StreamEnable(EmgStreams.Rms, true);
    await this.StreamEnable(EmgStreams.Ui, true);
    return true;
  }

  private static GetProgram(steps: string[], logger: LogService) : number[] {
    const encoder = new TextEncoder();
    const seperator: string = String.fromCharCode(0);
    let filteredSteps = steps;
    for(let i = 0; i < filteredSteps.length; i++) {
      if(filteredSteps[i].length < 3) {
        filteredSteps[i] += String.fromCharCode(32);
      }
    }
    let filteredStepsString = filteredSteps.join(seperator) + `${seperator}${seperator}${seperator}${seperator}${seperator}`;
    let byteSteps = encoder.encode(filteredStepsString);
    logger.warn(`GET PROGRAM INPUT ${steps} OUTPUT ${Array.from(byteSteps)}`, [{'SourceContext': 'BleEmg'}]);
    return Array.from(byteSteps);
  }

  private ResetData() {
    this.Readings = [];
  }

  private async EmgLoadProgram(program: number[]) {
    await this.SendCommand(CommandDefinitions.CmdWriteProgram, program, true);
  }

  public async unilateralMode(options) {
    let newOptions: ProgramStep[] = [];
    this.ProgramSteps.forEach((x, index) => {
      (index >= this.Pointer - 1) && (x.Dual = true);
      newOptions.push(x);
    });
    this.ProgramSteps = newOptions;
    await this.LoadProgramByString(options);
    await this.EmgProgramStep(this.Pointer - 1, true, true);
  }

  public async redoSegment(position: number) {
    // if we only removed the right side
    const unilateral = position !== this.Pointer - 1;
    await this.EmgProgramStep(position, true, true);
    unilateral && this.Readings.pop();
    !unilateral && delete this.ProgramSteps[this.Pointer - 1].LeftValue;
  }

  private async EmgProgramStep(position: number, step: boolean = true, redo?: boolean) {
    if(step) {
      await this.SendCommand(CommandDefinitions.CmdSetPc, [position], true);
      redo ? this.Pointer = position + 1 : this.Pointer++;
    } else {
      await this.SendCommand(CommandDefinitions.CmdSetPc, [position - 1], true);
    }
  }

  private async CompleteScan() {
    await this.StreamEnable(EmgStreams.Rms, false);
    await this.StreamEnable(EmgStreams.Ui, false);
  }

  async ProcessStatus(data: ArrayBuffer) {
    const view = new DataView(data);

    this.Status = {
      BatteryVoltage: view.getUint16(12, true),
      DcInputVoltage: view.getUint16(10, true),
      FirmwareMinor: view.getUint8(6),
      FirmwareMajor: view.getUint8(7),
      Firmware: '',
      Flag: view.getUint8(9),
      StreamStatus: view.getUint8(8),
      ProgramCounter: view.getUint8(14)
    };
    this.Status.Firmware = `${this.Status.FirmwareMajor}.${this.Status.FirmwareMinor}`;

    let streamStatus = view.getUint8(8)
    let flag = view.getUint8(9);
    let dcInputVolt = view.getUint16(10, true);
    let batterVolt = view.getUint16(12, true);
    this.ReportPower(batterVolt, 4120, 3400);
    this.ProgramCounter = view.getUint8(14);

    // only used for testing
    this.events.publish(ClaEvents.EMG_STATUS_RX, this.Status);
  }

  async ProcessConfig(data: ArrayBuffer) {
    try {
      const view = new DataView(data);
      let dataBytes = [];
      for(let i = 6; i < data.byteLength; i++){
        dataBytes.push(view.getUint8(i));
      }

      this.logger.debug(`rx Config ${data.byteLength}`, [{'SourceContext': 'BleEmg'}]);
      this.Config = {
        MyAddress: 1,
        SerialNumber: view.getUint32(7, true),
        XOffsetCounts: 300,
        XMaxCounts: 2047,
        YOffsetCounts: 200,
        YMaxCounts: 2047,
        LcdXDimension: 128,
        LcdYDimension: 160,
        TouchThreshold: 1500,
        RMSFilterDepth: 10,
        NotchFilter: view.getUint8(24),
        RMS1Gain: [view.getUint8(25), view.getUint8(26), view.getUint8(27)],
        RMS1Offset: [view.getUint8(28), view.getUint8(29), view.getUint8(30)],
        RMS2Gain: [view.getUint8(31), view.getUint8(32), view.getUint8(33)],
        RMS2Offset: [view.getUint8(34), view.getUint8(35), view.getUint8(36)],
        GraphMin: 0,
        GraphMax: view.getUint16(39, true),
        DisplayMode: view.getUint8(41),
      }


      this.logger.debug(`rx Config ${JSON.stringify(this.Config)}`, [{'SourceContext': 'BleEmg'}]);
      if(this.UpdateingNotch < 3 && this.UpdateingNotch > 0 && this.UpdateingNotch !== this.Config.NotchFilter) {
        this.logger.debug(`Notch Update Requested`, [{'SourceContext': 'BleEmg'}]);
        this.Config.NotchFilter = this.UpdateingNotch;
        await this.WriteConfig();
      }

      // only used for testing
      this.events.publish(ClaEvents.EMG_CONFIG_RX, this.Config);
    } catch (e) {
      this.logger.error(`ProcessConfig ERROR ${JSON.stringify(e)}`, [{'SourceContext': 'BleEmg'}]);
    }


  }

  async SwitchNotchFilterDepth(state: number) {
    this.logger.debug(`SwitchNotchFilterDepth request ${state}`, [{'SourceContext': 'BleEmg'}]);
    if (state > 2) state = 2;
    if (state < 0) state = 0;
    // 0 = off
    // 1 = 50Hz
    // 2 = 60Hz

    this.UpdateingNotch = state;

    await this.SendCommand(CommandDefinitions.CmdReadConfig, [], true);
    await delay(5000);
    await this.SendCommand(CommandDefinitions.CmdReadConfig, [], true);
    this.logger.debug(`SwitchNotchFilterDepth end ${state}`, [{'SourceContext': 'BleEmg'}]);
  }

  private getBytes(value: number, length: number, type: number) {
    let b = new ArrayBuffer(length);
    switch (type) {
      case 1: {
        new DataView(b).setUint32(0, value, true);
        break;
      }
      case 2: {
        new DataView(b).setFloat32(0, value, true);
        break;
      }
      case 3: {
        new DataView(b).setUint16(0, value, true);
        break;
      }
    }
    return Array.from(new Uint8Array(b));
  }

  public async WriteConfig() {
    let extras = [];
    extras.push(this.Config.MyAddress);
    extras.push(...this.getBytes(this.Config.SerialNumber, 4, 1));
    extras.push(...this.getBytes(this.Config.XOffsetCounts, 2, 3));
    extras.push(...this.getBytes(this.Config.XMaxCounts, 2, 3));
    extras.push(...this.getBytes(this.Config.YOffsetCounts, 2, 3));
    extras.push(...this.getBytes(this.Config.YMaxCounts, 2, 3));
    extras.push(this.Config.LcdXDimension);
    extras.push(this.Config.LcdYDimension);
    extras.push(...this.getBytes(this.Config.TouchThreshold, 2, 3));
    extras.push(this.Config.RMSFilterDepth);
    extras.push(this.Config.NotchFilter);
    extras.push(...this.Config.RMS1Gain);
    extras.push(...this.Config.RMS1Offset);
    extras.push(...this.Config.RMS2Gain);
    extras.push(...this.Config.RMS2Offset);
    extras.push(...this.getBytes(this.Config.GraphMin, 2, 3));
    extras.push(...this.getBytes(this.Config.GraphMax, 2, 3));
    extras.push(this.Config.DisplayMode);

    this.logger.debug(`Will write config ${JSON.stringify(extras)}`, [{'SourceContext': 'BleEmg'}]);
    await this.SendCommand(CommandDefinitions.CmdWriteConfig, extras);
  }

  async ProcessData(data: ArrayBuffer) {
    const view = new DataView(data);
    try {
      // Packet Header
      // byte 0 -  module address
      // byte 1 - streamID
      // byte 2 - packet type
      // byte 3-4 - msg id
      let msgId = view.getUint16(3, true);
      let streamType = view.getUint8(1);
      if(streamType == EmgStreams.Rms) {
        let rmsData: EmgDataReading = {
          leftValue: 0,
          rightValue: 0,
          eventMarkers: this.ProgramSteps[this.Pointer - 1].Location
        }

        // only used for testing
        this.events.publish(ClaEvents.EMG_DATA_RX, rmsData);

        let triggerFlag = view.getUint8(5) === 1;
        if(triggerFlag && !this.TriggerProcessing) {
          this.logger.debug('FOUND TRIGGER FLAG', [{'SourceContext': 'BleEmg'}]);
          await this.ProcessTrigger(this.CurrentReading);
        }
        rmsData.leftValue = view.getFloat32(6,true);
        rmsData.rightValue = view.getFloat32(10,true);
        this.CurrentReading = rmsData;
        // send to UI "StandardData"
        // SendHubResponse("StandardData", ScanTypes.Emg, transportData);
        this.events.publish(ClaEvents.SCANDATA_EMG_STANDARD, rmsData);
      } else if (streamType == EmgStreams.Ui) {
        let packetType = view.getUint8(2);
        if(packetType === 0 ) {
          // Pause request?
        } else if (packetType == 1) {
          await this.EmgProgramStep(view.getUint8(5));
          // send to UI "EmgPositionChange"
          // SendHubResponse("EmgPositionChange", data[5]);
          // this.events.publish(ClaEvents.SCANDATA_EMG_POSITION, this.Pointer - 1);
        }
      }

    } catch (error) {
      this.logger.error(`ProcessData ERROR ${JSON.stringify(error)}`, [{'SourceContext': 'BleEmg'}]);
    }
  }

  public async ProcessTrigger(reading: EmgDataReading) {
    this.TriggerProcessing = true;
    if(Date.now() - this.TriggerTimeout < 500) {
      this.logger.debug('Trigger Ignored', [{'SourceContext': 'BleEmg'}]);
      this.TriggerProcessing = false;
      return;
    }

    if(this.Pointer -1 >= this.ProgramSteps.length)
    {
      this.logger.debug('Trigger pulled after scan complete... Ignored', [{'SourceContext': 'BleEmg'}]);
      this.TriggerProcessing = false;
      return;
    }

    let proceed = false;
    let sendData = {
      leftReadings: 0,
      rightReadings: 0,
      eventMarkers: this.ProgramSteps[this.Pointer - 1].Location
    }
    if(this.ProgramSteps[this.Pointer - 1].Dual && !this.ProgramSteps[this.Pointer - 1].LeftComplete) {
      this.ProgramSteps[this.Pointer - 1].LeftValue = this.CurrentReading.leftValue;
      sendData.leftReadings = this.CurrentReading.leftValue;
      // setup transportData
    } else {
      proceed = true;
      if(this.ProgramSteps[this.Pointer - 1].Dual) {
        this.CurrentReading.leftValue = this.ProgramSteps[this.Pointer - 1].LeftValue;
      }

      let reading: EmgDataReading = {
        leftValue: this.CurrentReading.leftValue,
        rightValue: this.CurrentReading.rightValue,
        eventMarkers: this.ProgramSteps[this.Pointer - 1].Location
      }
      sendData.eventMarkers = reading.eventMarkers;
      sendData.rightReadings = reading.rightValue;
      sendData.leftReadings = reading.leftValue
      this.Readings.push(reading);
      // setup alternate transportData
    }
    this.events.publish(ClaEvents.SCANDATA_EMG_RX, sendData);
    await this.EmgProgramStep(this.Pointer, proceed);
    this.TriggerTimeout = Date.now();
    this.TriggerProcessing = false;
  }

  public async LoadProgramByString(encodedString: string) {
    const encoder = new TextEncoder();
    let data: Uint8Array = encoder.encode(encodedString);
    let steps: number[] = Array.from(data);
    this.HandheldProgramSteps = [];
    this.ProgramSteps = [];
    for(let i = steps.length - 1; i >= 0; i--) {
      if(steps[i] === 0) continue;

      let location: ProgramStep = new ProgramStep()
      location.Location = SpinalLocation[SpinalLocation[i]];
      location.Dual = steps[i] === 2;
      this.HandheldProgramSteps.push(SpinalLocation[location.Location]);
      this.ProgramSteps.push(location);
    }
    BleEmg.GetProgram(this.HandheldProgramSteps, this.logger);
  }
  public async changeScale(scale: number) {
    this.Config.GraphMax = scale;
    await this.WriteConfig();
  }
  /*
  public RmsValue: Subject<{left:number,right:number}> = new Subject<{left:number,right:number}>();

  private smoothQueue: {
    left:number[], right:number[]
  } = <{
    left:number[], right: number[]
  }>{left: [], right: []};

  public async SmoothRms(leftValue: number, rightValue: number) {
    this.smoothQueue.left.push(leftValue);
    this.smoothQueue.right.push(rightValue);
    if(this.smoothQueue.left.length > 10) {
      let leftAvg = this.smoothQueue.left.reduce((a,b) => {return a + b;}) / this.smoothQueue.left.length;
      let rightAvg = this.smoothQueue.right.reduce((a,b) => {return a + b;}) / this.smoothQueue.right.length;
      this.smoothQueue.left.shift();
      this.smoothQueue.right.shift();
      this.RmsValue.next({left:leftAvg,right: rightAvg});
    }
  }
  */
}

export enum EmgStreams
{
  Rms = 2,
  Ui = 3
}



// WEBPACK FOOTER //
// ./src/instruments/BleEmg.ts